import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from '../ui/Modal';
import ReadyHint from '../ui/ReadyHint';
import Button from '../ui/Button';
import PhoneInput from '../ui/PhoneInput';
import { isValidMobile } from '../../utils/phone';
import { clearPendingBookingCache } from '../../utils/pendingBooking';
import { pushEvent } from '../../utils/dataLayer';
import { googleSignInAvailable, renderGoogleButton } from '../../utils/googleSignIn';

// `summary` (optional) is what the visitor is about to lose or get: the checkout passes the price they hold and the offer timer.
// At checkout (`summary` given) it is Google only: no phone number is asked for before paying, and the Google e-mail is where the
// booking details are sent and what they log in with next time. The mobile number appears there only if Google cannot be used at all
// (no REACT_APP_GOOGLE_CLIENT_ID, or Google's script is blocked). The ordinary Sign in keeps the mobile number login, with Google as the
// other way in.
const GoogleG = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const LoginPopup = ({ onClose, proceedToCoupen, onClose1, summary = null, checkout = false }) => {
  const [mobile, setMobile] = useState(''); // the 10 digits, without +91
  const [showMobileErrors, setShowMobileErrors] = useState(false);
  const [isOtpSend, setIsOtpSend] = useState(false);
  const [otp, setOtp] = useState('');
  const userType = sessionStorage.getItem('userType')
  const [verifyOtpToken, setVerifyOtpToken] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [useMobile, setUseMobile] = useState(!googleSignInAvailable() || !checkout);
  const [googleFailed, setGoogleFailed] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [isGoogleBusy, setIsGoogleBusy] = useState(false);
  const googleButtonRef = useRef(null);

  // The buttons stay off until what they need is there; the line under the first button says what is left.
  const phoneMissing = [];
  if (!isValidMobile(mobile)) phoneMissing.push('a valid 10-digit mobile number');
  const otpReady = /^\d{4,8}$/.test(String(otp).trim());

  // Whatever way they came in: keep the login, clear what belonged to a previous login, and carry on.
  const finishLogin = (loginToken, message, event) => {
    localStorage.setItem('loginToken', loginToken);
    clearPendingBookingCache(); // this account may have a payment awaiting a session time
    pushEvent(event);
    if (message) toast(`${message}`, { type: 'success' });
    if (!onClose1) {
      onClose();
    }
    if (proceedToCoupen) {
      proceedToCoupen();
    }
    if (onClose1) {
      onClose1();
    }
  };

  const onGoogleCredential = async (credential) => {
    if (isGoogleBusy) return;
    setIsGoogleBusy(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/google-login`, { credential, userType });
      if (response.data.loginToken) finishLogin(response.data.loginToken, response.data.message, 'google_login');
    } catch (error) {
      console.error(error);
      toast(error?.response?.data?.message || 'Google sign-in did not work. Please try again or use your mobile number.', { type: 'error' });
    } finally {
      setIsGoogleBusy(false);
    }
  };

  // The latest handler is used even though Google keeps the one it was given when the button was drawn.
  const googleHandlerRef = useRef(onGoogleCredential);
  googleHandlerRef.current = onGoogleCredential;

  useEffect(() => {
    if (useMobile || isOtpSend || !googleButtonRef.current) return undefined;
    let cancelled = false;
    renderGoogleButton(googleButtonRef.current, (credential) => googleHandlerRef.current(credential)).then(() => {
      if (!cancelled) setGoogleReady(true);
    }).catch(() => {
      // Google's script could not load (blocked, offline): the mobile way is still there
      if (!cancelled) { setGoogleFailed(true); setUseMobile(true); }
    });
    return () => { cancelled = true; };
  }, [useMobile, isOtpSend]);

  const senOtpEmail = async (e) => {
    e.preventDefault();
    if (!isValidMobile(mobile)) {
      setShowMobileErrors(true);
      toast('Please enter a valid 10-digit mobile number', { type: 'warning' });
      return;
    }
    if (isSendingOtp) return;
    setIsSendingOtp(true);
    try {
      let response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/send-otp`, { phone:mobile });
      if (response.data.message) {
        toast(`${response.data.message}`, { type: 'success' });
        setVerifyOtpToken(response.data.otpVerifyToken);
        setIsOtpSend(true);
        pushEvent('otp_requested');
      }
    } catch (error) {
      console.error(error);
      // e.g. "Too many OTP requests..." (429) or "Please enter a valid mobile number" (400)
      toast(error?.response?.data?.message || 'Unable to send the OTP. Please try again.', { type: 'error' });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const createNewAccount = async (e) => {
    e.preventDefault();
    if (isVerifyingOtp || !otpReady) return;
    setIsVerifyingOtp(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/user-verify`,
        { otp, userType },
        {
          headers: {
            Authorization: `Bearer ${verifyOtpToken}`,
          },
        }
      );
      if (response.data.loginToken) {
        finishLogin(response.data.loginToken, response.data.message, 'otp_verified');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      toast(`${errorMessage}`, { type: 'error' });
      console.error(error);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleKeyPress1 = (event) => {
    if (event.key === 'Enter') {
      createNewAccount(event);
    }
  };

  const inputClass =
    'w-full rounded-none border border-forest/15 bg-white px-4 py-3.5 text-[15px] text-forest-deepest placeholder:text-forest-deepest/35 focus:border-brand-accent focus:outline-none';

  return (
    <>
      {/* At checkout the pop-up cannot be dismissed (no button, and a tap outside does nothing): it closes only by signing in. */}
      <Modal onClose={checkout ? undefined : onClose}>
        {!isOtpSend && !useMobile && (
          <div>
            <h2 className="text-center font-serif text-2xl italic text-forest-deepest">
              {checkout ? 'Get your booking details at' : 'Welcome to Bhavishyavani'}
            </h2>
            <p className="mt-2 text-center text-sm text-forest-deepest/65">
              {checkout
                ? 'Choose your Google account. Your booking link and session updates will be e-mailed to that inbox.'
                : 'Log in with your Google account.'}
            </p>
            {/* The visible button is ours (big, with a soft glow, the shape Google's brand guide shows). Google's own button sits on top of
                it, invisible and stretched to the same size, so a tap on ours is a real tap on Google's, and the sign-in stays Google's. */}
            <div className={`google-cta group relative mt-5 h-[56px] w-full ${googleReady ? 'cursor-pointer' : 'cursor-progress'}`} aria-busy={isGoogleBusy}>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-3 rounded-xl border-2 border-[#747775] bg-white text-[18px] font-medium text-[#1f1f1f] shadow-md transition-colors group-hover:bg-[#f4f6f6]" aria-hidden="true">
                <GoogleG />
                <span>Continue with Google</span>
              </div>
              <div ref={googleButtonRef} className="absolute inset-x-0 top-0 origin-top overflow-hidden opacity-[0.01]" style={{ transform: 'scaleY(1.4)' }} />
            </div>
            {summary && <div className="mt-3">{summary}</div>}
            {!checkout && (
              <button type="button" onClick={() => setUseMobile(true)} className="mx-auto mt-3 block text-sm text-forest-deepest/60 underline underline-offset-2 hover:text-forest-deepest/80">
                Use mobile number instead
              </button>
            )}
            {!checkout && (
              <button type="button" onClick={onClose} className="mx-auto mt-3 block text-xs text-forest-deepest/45 underline underline-offset-2 hover:text-forest-deepest/70">
                Not now
              </button>
            )}
          </div>
        )}
        {!isOtpSend && useMobile && (
          <form onSubmit={senOtpEmail}>
            <h2 className="text-center font-serif text-2xl italic text-forest-deepest">
              {checkout ? 'Log in to continue' : 'Welcome to Bhavishyavani'}
            </h2>
            <p className="mt-2 text-center text-sm text-forest-deepest/65">
              Log in with your mobile number to receive your booking information.
            </p>
            {summary && <div className="mt-4">{summary}</div>}
            <div className="my-5" />
            <PhoneInput
              id="login-mobile"
              value={mobile}
              onChange={setMobile}
              showErrors={showMobileErrors}
              inputClassName={inputClass}
              prefixClassName="border-forest/15 bg-canvas text-forest-deepest/70"
              placeholder="10-digit mobile number"
              autoFocus
            />
            <Button type="submit" variant="primary-dark" className="mt-4 w-full" loading={isSendingOtp} disabled={phoneMissing.length > 0}>
              Send my code by SMS
            </Button>
            <ReadyHint missing={phoneMissing} className="mt-2 text-center" />
            <p className="mt-3 text-center text-[11px] text-forest-deepest/50">
              No password. No spam.
            </p>
            {googleSignInAvailable() && !googleFailed && (
              <button type="button" onClick={() => setUseMobile(false)} className="mx-auto mt-3 block text-sm text-forest-deepest/60 underline underline-offset-2 hover:text-forest-deepest/80">
                Continue with Google instead
              </button>
            )}
            {!checkout && (
              <button type="button" onClick={onClose} className="mx-auto mt-2 block text-xs text-forest-deepest/45 underline underline-offset-2 hover:text-forest-deepest/70">
                Not now
              </button>
            )}
          </form>
        )}
        {isOtpSend && (
          <form onSubmit={createNewAccount}>
            <h2 className="text-center font-serif text-2xl italic text-forest-deepest">Enter your code</h2>
            <p className="mt-3 text-center text-sm text-forest-deepest/65">
              Code sent to <strong>+91 {mobile}</strong> by SMS.
            </p>
            {summary && <div className="mt-4">{summary}</div>}
            <div className="my-5" />
            <input
              value={otp}
              placeholder="Enter code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              autoFocus
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyPress={handleKeyPress1}
              className={inputClass}
              required
            />
            <Button type="submit" variant="primary-dark" className="mt-4 w-full" loading={isVerifyingOtp} disabled={!otpReady}>
              {checkout ? 'Verify and continue to payment' : 'Verify and continue'}
            </Button>
            <div className="mt-3 flex items-center justify-between text-xs text-forest-deepest/55">
              <button type="button" onClick={() => { setIsOtpSend(false); setOtp(''); }} className="underline underline-offset-2 hover:text-forest-deepest/80">Wrong number?</button>
              {!checkout && <button type="button" onClick={onClose} className="underline underline-offset-2 hover:text-forest-deepest/80">Not now</button>}
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};

export default LoginPopup;
